import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import axios from 'axios';

const FAQSection = ({ category, title = "Frequently Asked Questions" }) => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    loadFAQs();
  }, [category]);

  const loadFAQs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch from backend API (environment-aware)
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      console.log('FAQSection - Loading FAQs with:', { 
        category, 
        BACKEND_URL,
        fullUrl: `${BACKEND_URL}/api/faqs${category ? `?category=${category}` : ''}`
      });
      
      const response = await axios.get(
        `${BACKEND_URL}/api/faqs`,
        {
          params: category ? { category } : {},
          withCredentials: true
        }
      );

      console.log('FAQSection - Raw API response:', response.data);

      // Filter active FAQs and sort by order_index
      const activeFAQs = response.data
        .filter(faq => faq.is_active)
        .sort((a, b) => (a.order || 0) - (b.order || 0)); // Changed from order_index to order

      console.log('FAQSection - Filtered active FAQs:', activeFAQs);
      setFaqs(activeFAQs);
    } catch (err) {
      console.error('Error loading FAQs:', err);
      setError('Unable to load FAQs. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">
          {title}
        </h2>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
        </div>
      </div>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">
          {title}
        </h2>
        <div className="text-center text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">
          {error}
        </div>
      </div>
    );
  }

  // If no FAQs, show empty state
  if (faqs.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">
          {title}
        </h2>
        <div className="text-center text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-8">
          <p className="text-lg mb-2">No FAQs available at the moment.</p>
          <p className="text-sm text-gray-500">
            {category ? `Category: "${category}"` : 'All categories searched'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">
        {title}
      </h2>
      
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div 
            key={faq.id} 
            className="bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl border border-gray-200"
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
              <span className="text-base md:text-lg font-semibold pr-4 text-gray-900">
                {faq.question}
              </span>
              <ChevronDown 
                className={`w-5 h-5 flex-shrink-0 text-gold transition-transform duration-300 ${
                  openIndex === index ? 'transform rotate-180' : ''
                }`}
              />
            </button>
            
            <div 
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                openIndex === index ? 'max-h-[2000px]' : 'max-h-0'
              }`}
            >
              <div className="px-6 pb-5 pt-2">
                <div 
                  className="text-gray-700 text-sm md:text-base leading-relaxed whitespace-pre-wrap break-words"
                  dangerouslySetInnerHTML={{ 
                    __html: faq.answer.replace(/\n/g, '<br/>') 
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQSection;
